import { CircularProgress } from '@civilgrid/ui/components';
import { type ButtonHTMLAttributes, forwardRef, type ReactNode, useMemo } from 'react';
import Icon, { type IconName } from '~/components2/Icon';

export type ButtonSize = 'small' | 'standard' | 'big' | 'icon';

export interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  alternate?: boolean;
  danger?: boolean;
  icon?: IconName;
  iconSize?: number;
  isProcessing?: boolean;
  dark?: boolean;
  ghost?: boolean;
  size?: ButtonSize;
  className?: string;
}

const Button = forwardRef<HTMLButtonElement, Props>(
  (
    { children, alternate, danger, icon, iconSize, isProcessing, dark, ghost, size = 'standard', className, ...props },
    ref,
  ) => {
    // Structural base only (match UI Button) – no variant colors so extraStyles can override cleanly
    let commonClassNames =
      'font-noto inline-flex items-center justify-center whitespace-nowrap rounded-[6px] font-medium gap-1 text-xs leading-5 transition-all cursor-pointer focus-ring disabled:cursor-not-allowed shrink-0 [&_svg]:shrink-0';

    let containerClassNames = '';

    const extraStyles = useMemo(() => {
      let styles = '';
      if (alternate === true) {
        // alternate = UI secondary
        styles +=
          'bg-grey-10 border border-grey-30 text-primary-dark hover:bg-grey-30 disabled:bg-grey-20 disabled:text-grey-50 disabled:border-transparent disabled:hover:bg-grey-20 ';
      } else if (danger === true) {
        // danger = UI danger
        styles +=
          'bg-grey-10 border border-grey-30 text-red-50 font-semibold hover:bg-red-60 hover:text-white disabled:bg-grey-20 disabled:text-grey-50 disabled:border-transparent disabled:hover:bg-grey-20 ';
      } else if (ghost === true) {
        // ghost = UI hollow
        styles +=
          'bg-transparent text-primary-dark hover:bg-grey-20 border border-transparent disabled:bg-grey-20 disabled:text-grey-50 disabled:border-transparent disabled:hover:bg-grey-20 ';
      } else if (dark === true) {
        styles +=
          '!text-white !border-black hover:bg-gray-800 hover:!opacity-100 disabled:bg-grey-20 disabled:text-grey-50 disabled:border-transparent disabled:hover:bg-grey-20 ';
      } else {
        // default = UI primary
        styles +=
          'bg-primary text-white border border-transparent hover:bg-primary-dark disabled:bg-grey-20 disabled:text-grey-50 disabled:border-transparent disabled:hover:bg-grey-20 ';
      }

      // Size padding (matches UI Button size variants)
      switch (size) {
        case 'small':
          styles += 'px-2 py-0.5 ';
          break;
        case 'big':
          styles += 'px-3 py-2 ';
          break;
        case 'icon':
          styles += 'p-1.5 ';
          break;
        default:
          styles += 'px-2 py-1 ';
          break;
      }
      return styles.trim();
    }, [danger, alternate, ghost, dark, size]);

    if (children && size !== 'icon') {
      iconSize = iconSize || 14;
      commonClassNames = `${commonClassNames} min-w-[100px]`;
      containerClassNames = 'flex items-center justify-center space-x-1 px-2';
    } else {
      // just icon
      iconSize = iconSize || 16;
      containerClassNames = 'flex items-center text-base';
    }

    let content: ReactNode;

    if (isProcessing) {
      content = (
        <span className={containerClassNames}>
          {children && <span className="sr-only">{children}</span>}
          <CircularProgress
            indeterminate
            tone={props.disabled ? 'neutral' : alternate || danger || ghost ? 'brand' : 'inverse'}
            size={iconSize}
          />
        </span>
      );
    } else {
      content = (
        <span className={containerClassNames}>
          {icon && <Icon icon={icon} size={iconSize} />}
          <span>{children}</span>
        </span>
      );
    }

    return (
      <button
        ref={ref}
        // ensure forms don't get accidentally submitted.
        // If other types were passed in, they will overriden on the next line
        type="button"
        {...props}
        className={`${commonClassNames} ${extraStyles} ${className ?? ''}`.trim()}
      >
        {content}
      </button>
    );
  },
);

Button.displayName = 'Button';
export default Button;
